import { v4 as uuidv4 } from 'uuid';
import apiClient from '../api/ApiClient';
import { ApiEndpoints } from '../../config/constants';

export enum ActivityType {
    PAGE_VIEW = 'page_view',
    PRODUCT_VIEW = 'product_view',
    NAVIGATION = 'navigation',
    CATEGORY_FILTERING = 'category_filtering',
    CART_ADD = 'cart_add',
    CHECKOUT_START = 'checkout_start',
    CHECKOUT_COMPLETE = 'checkout_complete',
    SIGN_UP = 'sign_up',
    SIGN_IN = 'sign_in',
    SIGN_OUT = 'sign_out',
}

interface TrackActivityParams {
    activityType: ActivityType;
    screenName?: string;
    screenPath?: string;
    activityData?: Record<string, any>;
}

interface DeviceInfo {
    deviceType: string;
    browser: string;
    os: string;
    userAgent: string;
}

class TrackingService {
    private anonymousId: string | null = null;
    private sessionId: string | null = null;
    private initialized: boolean = false;
    private initPromise: Promise<void> | null = null;
    private debounceTimers: Map<string, NodeJS.Timeout> = new Map(); // Debounce timers per activity

    /**
     * Initialize tracking service
     * Gets or creates anonymous ID and session ID
     * Uses promise lock to prevent duplicate initialization in React Strict Mode
     */
    public async initialize(): Promise<void> {
        // Return existing promise if initialization is in progress
        if (this.initPromise) {
            return this.initPromise;
        }

        // Return immediately if already initialized
        if (this.initialized) {
            return;
        }

        // Create initialization promise
        this.initPromise = this.performInitialization();

        try {
            await this.initPromise;
        } finally {
            this.initPromise = null;
        }
    }

    /**
     * Actual initialization logic
     */
    private async performInitialization(): Promise<void> {
        try {
            // Get or create anonymous ID
            this.anonymousId = this.getOrCreateAnonymousId();
            
            // Get or create session ID (expires after browser close)
            this.sessionId = this.getOrCreateSessionId();

            // Register or update anonymous user in backend
            await this.registerAnonymousUser();

            this.initialized = true;
        } catch (error) {
            console.error('❌ Error initializing tracking service:', error);
            this.initialized = false; // Allow retry on error
        }
    }

    /**
     * Get or create anonymous ID from localStorage
     * This persists across sessions
     */
    private getOrCreateAnonymousId(): string {
        const stored = localStorage.getItem('anonymousId');
        
        if (stored) {
            return stored;
        }

        const newId = uuidv4();
        
        localStorage.setItem('anonymousId', newId);
        return newId;
    }

    /**
     * Get or create session ID from sessionStorage
     * This expires when browser closes
     */
    private getOrCreateSessionId(): string {
        const stored = sessionStorage.getItem('sessionId');
        
        if (stored) {
            return stored;
        }

        const newId = uuidv4();
        
        sessionStorage.setItem('sessionId', newId);
        return newId;
    }

    /**
     * Get device information from user agent
     */
    private getDeviceInfo(): DeviceInfo {
        const userAgent = navigator.userAgent;
        
        // Detect device type
        let deviceType = 'desktop';
        
        if (/Mobile|Android|iPhone|iPod/.test(userAgent)) {
            deviceType = 'mobile';
        } else if (/iPad|Tablet/.test(userAgent)) {
            deviceType = 'tablet';
        }

        // Detect browser
        let browser = 'Unknown';
        
        if (userAgent.includes('Firefox')) {
            browser = 'Firefox';
        } else if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
            browser = 'Chrome';
        } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
            browser = 'Safari';
        } else if (userAgent.includes('Edg')) {
            browser = 'Edge';
        } else if (userAgent.includes('MSIE') || userAgent.includes('Trident')) {
            browser = 'Internet Explorer';
        }

        // Detect OS
        let os = 'Unknown';
        
        if (userAgent.includes('Win')) {
            os = 'Windows';
        } else if (userAgent.includes('Mac')) {
            os = 'MacOS';
        } else if (userAgent.includes('Linux')) {
            os = 'Linux';
        } else if (userAgent.includes('Android')) {
            os = 'Android';
        } else if (userAgent.includes('iOS') || userAgent.includes('iPhone') || userAgent.includes('iPad')) {
            os = 'iOS';
        }

        return {
            deviceType,
            browser,
            os,
            userAgent
        };
    }

    /**
     * Register anonymous user with backend
     */
    private async registerAnonymousUser(): Promise<void> {
        if (!this.anonymousId) {
            return;
        }

        try {
            const deviceInfo = this.getDeviceInfo();
            
            await apiClient.postPublic(ApiEndpoints.Analytics.RegisterAnonymous, {
                anonymousId: this.anonymousId,
                ...deviceInfo,
                referrer: document.referrer,
                landingPage: window.location.pathname + window.location.search
            });
        } catch (error) {
            console.error('❌ Error registering anonymous user:', error);
        }
    }

    /**
     * Track user activity with debouncing to prevent duplicates
     */
    public async trackActivity(params: TrackActivityParams): Promise<void> {
        if (!this.initialized) {
            await this.initialize();
        }

        if (!this.anonymousId) {
            console.warn('⚠️ Cannot track activity: No anonymous ID');
            return;
        }

        // Create unique key for this activity
        const trackingKey = `${params.activityType}-${params.screenName || ''}-${params.screenPath || ''}`;

        // Clear existing timer for this activity
        const existingTimer = this.debounceTimers.get(trackingKey);
        if (existingTimer) {
            clearTimeout(existingTimer);
        }

        // Set new debounced timer
        const timer = setTimeout(async () => {
            try {
                const deviceInfo = this.getDeviceInfo();
                
                await apiClient.postPublic(ApiEndpoints.Analytics.TrackActivity, {
                    anonymousId: this.anonymousId,
                    sessionId: this.sessionId,
                    activityType: params.activityType,
                    screenName: params.screenName,
                    screenPath: params.screenPath || window.location.pathname,
                    activityData: params.activityData,
                    deviceType: deviceInfo.deviceType,
                    browser: deviceInfo.browser
                });

                // Remove timer after execution
                this.debounceTimers.delete(trackingKey);
            } catch (error) {
                console.error('❌ Error tracking activity:', error);
            }
        }, 1000); // 1second debounce

        this.debounceTimers.set(trackingKey, timer);
    }

    /**
     * Track page view
     */
    public async trackPageView(screenName?: string, screenPath?: string): Promise<void> {
        await this.trackActivity({
            activityType: ActivityType.PAGE_VIEW,
            screenName,
            screenPath: screenPath || window.location.pathname
        });
    }

    /**
     * Track product view
     */
    public async trackProductView(productId: string, productData?: Record<string, any>): Promise<void> {
        await this.trackActivity({
            activityType: ActivityType.PRODUCT_VIEW,
            screenName: 'Product',
            activityData: {
                productId,
                ...productData
            }
        });
    }

    /**
     * Track cart add
     */
    public async trackCartAdd(productId: string, quantity: number, productData?: Record<string, any>): Promise<void> {
        await this.trackActivity({
            activityType: ActivityType.CART_ADD,
            screenName: 'Cart',
            activityData: {
                productId,
                quantity,
                ...productData
            }
        });
    }

    /**
     * Track checkout events
     */
    public async trackCheckoutStart(orderData?: Record<string, any>): Promise<void> {
        await this.trackActivity({
            activityType: ActivityType.CHECKOUT_START,
            screenName: 'Checkout',
            activityData: orderData
        });
    }

    public async trackCheckoutComplete(orderId: string, orderData?: Record<string, any>): Promise<void> {
        await this.trackActivity({
            activityType: ActivityType.CHECKOUT_COMPLETE,
            screenName: 'Checkout',
            activityData: {
                orderId,
                ...orderData
            }
        });
    }

    /**
     * Track authentication events
     */
    public async trackSignUp(userId: string): Promise<void> {
        await this.trackActivity({
            activityType: ActivityType.SIGN_UP,
            screenName: 'SignUp',
            activityData: { userId }
        });
        
        // Link anonymous user to authenticated user
        await this.linkAnonymousUser(userId);

    }

    public async trackSignIn(userId: string): Promise<void> {
        await this.trackActivity({
            activityType: ActivityType.SIGN_IN,
            screenName: 'SignIn',
            activityData: { userId }
        });
        
        // Link anonymous user to authenticated user
        await this.linkAnonymousUser(userId);

    }

    public async trackSignOut(): Promise<void> {
        await this.trackActivity({
            activityType: ActivityType.SIGN_OUT,
            screenName: 'SignOut'
        });
    }

    /**
     * Link anonymous user to authenticated user
     */
    private async linkAnonymousUser(userId: string): Promise<void> {
        if (!this.anonymousId) {
            return;
        }

        try {
            await apiClient.postPublic(ApiEndpoints.Analytics.LinkUser, {
                anonymousId: this.anonymousId,
                userId
            });
        } catch (error) {
            console.error('❌ Error linking anonymous user:', error);
        }
    }

    /**
     * Get current anonymous ID
     */
    public getAnonymousId(): string | null {
        return this.anonymousId;
    }

    /**
     * Get current session ID
     */
    public getSessionId(): string | null {
        return this.sessionId;
    }

    /**
     * Reset session (create new session ID)
     */
    public resetSession(): void {
        sessionStorage.removeItem('sessionId');
        this.sessionId = this.getOrCreateSessionId();
        console.log('🔗 Session reset', {
            sessionId: this.sessionId
        });
    }
}

// Export singleton instance
export const trackingService = new TrackingService();
export default trackingService;


